import { downloadBlobFile } from '../../core/utils/browser-file.util';

export interface PaymentReceiptData {
  txn: string;
  monthLabel: string;
  paymentDate: string;
  createdOn: string;
  paymentMethod: string;
  status: string;
  organizationName?: string;
  flatName: string;
  floorName: string;
  houseName: string;
  renterName: string;
  amountRows: Array<[string, number]>;
  total: number;
  brandName?: string;
  brandLogoUrl?: string | null;
  generatedOn: string;
  generatedBy: string;
  developedBy: string;
}

type PaymentDocumentType = 'receipt' | 'slip';

interface PaymentDocumentOptions {
  type: PaymentDocumentType;
  title: string;
  subject: string;
  systemNote: string;
  status: string;
}

type PdfImageFormat = 'PNG' | 'JPEG' | 'WEBP';

function sanitizeReceiptFileNamePart(value: string | null | undefined): string {
  const normalized = `${value ?? ''}`
    .trim()
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/&/g, ' and ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalized || 'na';
}

function buildReceiptFileNameBase(data: PaymentReceiptData, documentType: PaymentDocumentType): string {
  const renterName = sanitizeReceiptFileNamePart(data.renterName);
  const paymentMonthYear = sanitizeReceiptFileNamePart(data.monthLabel);
  const paymentDate = sanitizeReceiptFileNamePart(data.paymentDate);
  const houseName = sanitizeReceiptFileNamePart(data.houseName);
  const floorName = sanitizeReceiptFileNamePart(data.floorName);
  const flatName = sanitizeReceiptFileNamePart(data.flatName);
  const txn = sanitizeReceiptFileNamePart(data.txn);
  const prefix = documentType === 'slip' ? 'payment-slip' : 'payment-receipt';

  return `${prefix}-${renterName}-${paymentMonthYear}-${houseName}-${floorName}-${flatName}-cd-${paymentDate}-TRN-${txn}`;
}

async function loadImageForPdf(path: string): Promise<{ dataUrl: string; format: PdfImageFormat } | null> {
  try {
    if (!path) {
      return null;
    }

    if (path.startsWith('data:image/')) {
      return {
        dataUrl: path,
        format: detectPdfImageFormatFromMime(path.slice(5, path.indexOf(';')))
      };
    }

    const response = await fetch(path);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    const format = detectPdfImageFormatFromMime(blob.type);
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to read logo image.'));
      reader.readAsDataURL(blob);
    });
    return { dataUrl, format };
  } catch {
    return null;
  }
}

function detectPdfImageFormatFromMime(mimeType: string): PdfImageFormat {
  const normalized = `${mimeType ?? ''}`.trim().toLowerCase();
  if (normalized.includes('png')) {
    return 'PNG';
  }
  if (normalized.includes('webp')) {
    return 'WEBP';
  }
  return 'JPEG';
}

export async function downloadPaymentReceiptPdf(data: PaymentReceiptData): Promise<void> {
  const { doc, fileName } = await buildReceiptPdfDoc(data, receiptDocumentOptions());
  downloadBlobFile(doc.output('blob') as Blob, fileName);
}

export async function printPaymentReceiptPdf(data: PaymentReceiptData): Promise<void> {
  const { doc } = await buildReceiptPdfDoc(data, receiptDocumentOptions());
  doc.autoPrint();
  const blobUrl = doc.output('bloburl');
  window.open(blobUrl, '_blank');
}

export async function downloadPaymentSlipPdf(data: PaymentReceiptData): Promise<void> {
  const { doc, fileName } = await buildReceiptPdfDoc(data, slipDocumentOptions());
  downloadBlobFile(doc.output('blob') as Blob, fileName);
}

export async function printPaymentSlipPdf(data: PaymentReceiptData): Promise<void> {
  const { doc } = await buildReceiptPdfDoc(data, slipDocumentOptions());
  doc.autoPrint();
  const blobUrl = doc.output('bloburl');
  window.open(blobUrl, '_blank');
}

export async function buildPaymentReceiptPdfAttachment(data: PaymentReceiptData): Promise<{
  fileName: string;
  pdfBase64: string;
}> {
  const { doc, fileName } = await buildReceiptPdfDoc(data, receiptDocumentOptions());
  const dataUri = doc.output('datauristring');
  const base64 = `${dataUri ?? ''}`.split(',')[1] || '';
  return {
    fileName,
    pdfBase64: base64
  };
}

function receiptDocumentOptions(): PaymentDocumentOptions {
  return {
    type: 'receipt',
    title: 'Payment Receipt',
    subject: 'Payment Receipt',
    systemNote: 'System-generated receipt',
    status: 'PAID'
  };
}

function slipDocumentOptions(): PaymentDocumentOptions {
  return {
    type: 'slip',
    title: 'Payment Slip',
    subject: 'Payment Slip',
    systemNote: 'System-generated payment slip',
    status: 'UNPAID'
  };
}

async function buildReceiptPdfDoc(data: PaymentReceiptData, options: PaymentDocumentOptions): Promise<{ doc: any; fileName: string }> {
  const brandName = `${data.brandName ?? ''}`.trim() || 'HTALLY';
  const brandLogoUrl = `${data.brandLogoUrl ?? ''}`.trim();
  const organizationName = `${data.organizationName ?? ''}`.trim();
  const fallbackLogoPath = !brandLogoUrl && brandName === 'HTALLY' ? '/HTallyIcon.png' : '';
  const documentData: PaymentReceiptData = {
    ...data,
    status: options.status
  };
  const fileNameBase = buildReceiptFileNameBase(documentData, options.type);
  const fileName = `${fileNameBase}.pdf`;

  const [{ jsPDF }, logoImage] = await Promise.all([
    import('jspdf'),
    loadImageForPdf(brandLogoUrl || fallbackLogoPath)
  ]);

  const rows: Array<[string, string]> = [
    ['Transaction No', documentData.txn],
    ['Payment Month', documentData.monthLabel],
    ['Payment Date', documentData.paymentDate],
    ['Created On', documentData.createdOn],
    ['Payment Method', documentData.paymentMethod],
    ...(organizationName ? [['Organization', organizationName] as [string, string]] : []),
    ['House', documentData.houseName],
    ['Floor', documentData.floorName],
    ['Flat', documentData.flatName],
    ['Renter', documentData.renterName]
  ];

  const receiptWidth = 226.8; // 80mm thermal width
  const left = 10;
  const right = receiptWidth - 10;
  const contentWidth = right - left;
  const estimatedRowsHeight = rows.length * 20;
  const estimatedAmountHeight = (data.amountRows.length + 2) * 14;
  const estimatedHeight = 190 + estimatedRowsHeight + estimatedAmountHeight + 76;
  const receiptHeight = Math.max(350, estimatedHeight);

  const doc = new jsPDF({ unit: 'pt', format: [receiptWidth, receiptHeight] });
  doc.setProperties({
    title: fileNameBase,
    subject: options.subject
  });
  let y = 20;

  if (logoImage) {
    const logoWidth = 34;
    const logoHeight = 34;
    const logoX = (receiptWidth - logoWidth) / 2;
    doc.addImage(logoImage.dataUrl, logoImage.format, logoX, y - 2, logoWidth, logoHeight);
    y += 40;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(80, 93, 124);
  doc.text(options.title, receiptWidth / 2, y, { align: 'center' });

  y += 12;
  doc.setDrawColor(190, 200, 220);
  doc.line(left, y, right, y);
  y += 10;

  rows.forEach(([label, value]) => {
    const safeValue = value || 'N/A';
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 93, 124);
    doc.setFontSize(8);
    doc.text(`${label}:`, left, y);
    doc.setTextColor(31, 38, 65);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    const wrappedValue = doc.splitTextToSize(safeValue, contentWidth * 0.56);
    doc.text(wrappedValue, left + 78, y);
    y += Math.max(12, wrappedValue.length * 10);
  });

  doc.setDrawColor(220, 225, 240);
  doc.line(left, y, right, y);
  y += 10;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(31, 38, 65);
  doc.text('Amount Details', left, y);
  y += 10;

  documentData.amountRows.forEach(([label, amount]) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text(label, left, y);
    doc.text(Number(amount ?? 0).toLocaleString('en-US'), right, y, { align: 'right' });
    y += 12;
  });

  doc.setDrawColor(220, 225, 240);
  doc.line(left, y, right, y);
  y += 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text('Total', left, y);
  doc.text(Number(documentData.total ?? 0).toLocaleString('en-US'), right, y, { align: 'right' });

  y += 20;
  drawStatusBadge(doc, documentData.status, left, right, y);

  y += 24;
  doc.setDrawColor(220, 225, 240);
  doc.line(left, y, right, y);
  y += 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(95, 111, 150);
  doc.text(options.systemNote, receiptWidth / 2, y, { align: 'center' });

  y += 10;
  doc.setFontSize(6.7);
  doc.text(`Generated On: ${documentData.generatedOn}`, receiptWidth / 2, y, { align: 'center' });
  y += 9;
  doc.text(`Generated By: ${documentData.generatedBy}`, receiptWidth / 2, y, { align: 'center' });
  y += 9;
  doc.text(`Developed by: ${documentData.developedBy}`, receiptWidth / 2, y, { align: 'center' });
  return { doc, fileName };
}

function drawStatusBadge(doc: any, status: string, left: number, right: number, y: number): void {
  const normalizedStatus = `${status ?? ''}`.trim().toUpperCase();
  const isPaid = normalizedStatus === 'PAID';
  const badgeText = normalizedStatus || 'N/A';
  const label = 'Status';
  const badgeWidth = Math.max(48, badgeText.length * 7 + 18);
  const badgeHeight = 16;
  const badgeX = right - badgeWidth;
  const badgeY = y - 11;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(31, 38, 65);
  doc.text(label, left, y);

  if (isPaid) {
    doc.setFillColor(220, 252, 231);
    doc.setTextColor(22, 101, 52);
  } else {
    doc.setFillColor(254, 226, 226);
    doc.setTextColor(153, 27, 27);
  }

  doc.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 4, 4, 'F');
  doc.text(badgeText, badgeX + badgeWidth / 2, y, { align: 'center' });
}
